import React from 'react';
import { Alert, Collapse, Tag } from 'antd';
import { CalculatorOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatMontant } from '../../../variables/constants';
import { libellesTraduits } from '../../../i18n/libelles';

const INK = '#18181b';
const MUTED = '#52525b';
const FAINT = '#a1a1aa';
const LINE = '#e4e4e7';
const PALE = '#fafafa';
const ACCENT = '#1a6b3c';

const LIENS = libellesTraduits('dossierFinance:detailCalcul.liens');

const titre = (texte) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, margin: '14px 0 6px' }}>
    {texte}
  </div>
);

/**
 * Comment le montant de l'offre est obtenu, tel que le serveur l'a calculé.
 *
 * L'offre n'affichait que les chefs de préjudice non nuls : une ITT de trente jours
 * disparaissait de l'écran sans que rien ne dise pourquoi. Chaque chef est ici listé,
 * avec l'article, la formule et le calcul fait - ou le motif quand il vaut 0 -, après
 * les données retenues (SMIG, revenu, âge, expertise). `detail` vient de l'offre
 * (`detailCalcul`) : c'est le calcul qui a produit le montant, pas une reconstitution.
 */
export default function DetailCalculOffre({ detail }) {
  const { t } = useTranslation('dossierFinance');
  if (!detail) {
    return (
      <Alert type="info" showIcon style={{ borderRadius: 6, marginTop: 12, fontSize: 12 }}
        message={t('detailCalcul.indisponible')}
        description={t('detailCalcul.indisponibleTexte')} />
    );
  }

  const chefs = detail.chefs || [];
  const retenus = chefs.filter((c) => Number(c.montant) > 0);
  const nuls = chefs.filter((c) => !(Number(c.montant) > 0));

  const ligneChef = (c, i) => (
    <div key={`${c.article}-${c.libelle}-${i}`} style={{ padding: '8px 0', borderBottom: `1px solid ${LINE}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <Tag style={{ borderRadius: 4, fontSize: 10.5, margin: 0 }}>{c.article}</Tag>
        <span style={{ fontWeight: 600, color: INK, flex: 1 }}>{c.libelle}</span>
        <span style={{ fontWeight: 700, color: Number(c.montant) > 0 ? INK : FAINT, fontVariantNumeric: 'tabular-nums' }}>
          {formatMontant(c.montant)}
        </span>
      </div>
      <div style={{ fontSize: 12, color: MUTED, marginTop: 3 }}>{c.formule}</div>
      {c.calcul && (
        <div style={{ fontSize: 12, color: ACCENT, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>= {c.calcul}</div>
      )}
      {c.motif && <div style={{ fontSize: 12, color: '#92400e', marginTop: 2 }}>{c.motif}</div>}
    </div>
  );

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: INK, fontSize: 14 }}>
        <CalculatorOutlined style={{ color: ACCENT }} /> {t('detailCalcul.titre')}
      </div>

      {titre(t('detailCalcul.donnees'))}
      <div style={{ border: `1px solid ${LINE}`, borderRadius: 6, overflow: 'hidden' }}>
        {(detail.donnees || []).map((d, i) => (
          <div key={d.libelle} style={{
            display: 'flex', gap: 10, padding: '6px 10px', fontSize: 12.5,
            background: i % 2 ? PALE : '#fff', flexWrap: 'wrap',
          }}>
            <span style={{ color: MUTED, minWidth: 150 }}>{d.libelle}</span>
            <span style={{ color: INK, fontWeight: 600 }}>{d.valeur}</span>
            {d.precision && <span style={{ color: FAINT, flexBasis: '100%', paddingLeft: 160, marginTop: -2 }}>{d.precision}</span>}
          </div>
        ))}
      </div>

      {titre(t('detailCalcul.chefsRetenus', { nombre: retenus.length }))}
      {retenus.length ? retenus.map(ligneChef)
        : <div style={{ fontSize: 12.5, color: FAINT }}>{t('detailCalcul.aucunChef')}</div>}

      {nuls.length > 0 && (
        <Collapse ghost size="small" style={{ marginTop: 6 }} items={[{
          key: 'nuls',
          label: <span style={{ fontSize: 12.5, color: MUTED }}>{t('detailCalcul.chefsNuls', { nombre: nuls.length })}</span>,
          children: nuls.map(ligneChef),
        }]} />
      )}

      {(detail.ayantsDroit || []).length > 0 && (
        <>
          {titre(t('detailCalcul.partAyantsDroit'))}
          {detail.ayantsDroit.map((a, i) => (
            <div key={`${a.nom}-${i}`} style={{ padding: '8px 0', borderBottom: `1px solid ${LINE}`, fontSize: 12.5 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ fontWeight: 600, color: INK, flex: 1 }}>
                  {a.nom || t('detailCalcul.ayantDroit')} <span style={{ color: FAINT, fontWeight: 400 }}>· {LIENS[a.lien] || a.lien}{a.age != null ? `, ${t('detailCalcul.age', { age: a.age })}` : ''}</span>
                </span>
                <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatMontant(a.total)}</span>
              </div>
              <div style={{ color: MUTED, marginTop: 2 }}>
                {t('detailCalcul.economique', { montant: formatMontant(a.prejudiceEconomique), calcul: a.calculEconomique })}
              </div>
              <div style={{ color: MUTED }}>
                {t('detailCalcul.moral', { montant: formatMontant(a.prejudiceMoral), calcul: a.calculMoral })}
              </div>
            </div>
          ))}
        </>
      )}

      {(detail.plafonds || []).length > 0 && (
        <Alert type="warning" showIcon style={{ borderRadius: 6, marginTop: 10, fontSize: 12 }}
          message={t('detailCalcul.plafonds')} description={<ul style={{ margin: 0, paddingLeft: 18 }}>{detail.plafonds.map((p) => <li key={p}>{p}</li>)}</ul>} />
      )}

      {titre(t('detailCalcul.totaux'))}
      <div style={{ border: `1px solid ${LINE}`, borderRadius: 6, overflow: 'hidden' }}>
        {(detail.totaux || []).map((t, i, tous) => {
          const dernier = i === tous.length - 1;
          const aMontant = t.montant !== null && t.montant !== undefined && !t.libelle.startsWith('Taux');
          return (
            <div key={t.libelle} style={{
              padding: '7px 10px', background: dernier ? '#f0fdf4' : (i % 2 ? PALE : '#fff'),
              borderTop: i ? `1px solid ${LINE}` : 'none',
            }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ flex: 1, fontWeight: dernier ? 700 : 600, color: dernier ? ACCENT : INK }}>{t.libelle}</span>
                {aMontant && (
                  <span style={{ fontWeight: dernier ? 800 : 600, color: dernier ? ACCENT : INK, fontVariantNumeric: 'tabular-nums' }}>
                    {formatMontant(t.montant)}
                  </span>
                )}
              </div>
              {t.calcul && <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{t.calcul}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
